import cx from 'classnames';
import type { FunctionComponent } from 'react';

import styles from './Card.module.less';
import { defineSlotComponent, findAllForSlot, findFirstForSlot } from '../../layouts/slots';

export type Props = {
  variant?: 'outline' | 'fill';
  isFullWidth?: boolean;
  isContentFullHeight?: boolean;
  padding?: 'none' | 'xs' | 'small' | 'medium' | 'large';
  /**
   * Background color for the filled variant. If none is provided, the default gray will be used.
   */
  color?: 'blue' | 'pink' | 'yellow';
};

type CardComponent = FunctionComponent<Props> & {
  Header: FunctionComponent;
  SubHeader: FunctionComponent;
  Content: FunctionComponent;
};

/**
 * A simple Card component to give us a standardized style of contents.
 *
 * @example
 * <Card>
 *  <Card.Header>The title of my card!</Card.Header>
 *  <Card.SubHeader>The subheader of my card!</Card.SubHeader>
 *  <Card.Content><MyArbitraryComponent /></Card.Content>
 * </Card>
 */
const Card: CardComponent = ({
  variant = 'outline',
  isFullWidth,
  children,
  isContentFullHeight,
  color,
  padding = 'large',
}) => {
  const header = findFirstForSlot('header', children);
  const subheader = findFirstForSlot('subheader', children);
  const content = findAllForSlot('content', children);

  return (
    <div
      className={cx(styles.card, {
        [styles.outline]: variant === 'outline',
        [styles.fill]: variant === 'fill',
        [styles.fitContent]: !isFullWidth,
        [styles.fullWidth]: isFullWidth,
        [styles.blue]: variant === 'fill' && color === 'blue',
        [styles.pink]: variant === 'fill' && color === 'pink',
        [styles.yellow]: variant === 'fill' && color === 'yellow',
        [styles.paddingNone]: padding === 'none',
        [styles.paddingXS]: padding === 'xs',
        [styles.paddingSmall]: padding === 'small',
        [styles.paddingMedium]: padding === 'medium',
        [styles.paddingLarge]: padding === 'large',
      })}
    >
      {header && (
        <div className={styles.header}>
          {header}
          {subheader && <div className={styles.subheader}>{subheader}</div>}
        </div>
      )}
      <div
        className={cx(styles.content, {
          [styles.fullHeight]: !!isContentFullHeight,
        })}
        >
        {content}
      </div>
    </div>
  );
};

Card.Header = defineSlotComponent('header', ({ children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment, @typescript-eslint/comma-dangle
  <>{children}</>
);
Card.Header.displayName = 'Card.Header';

Card.SubHeader = defineSlotComponent('subheader', ({ children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment, @typescript-eslint/comma-dangle
  <>{children}</>
);
Card.SubHeader.displayName = 'Card.SubHeader';

Card.Content = defineSlotComponent('content', ({ children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment, @typescript-eslint/comma-dangle
  <>{children}</>
);
Card.Content.displayName = 'Card.Content';

export default Card;
